
import React, { createContext, useState, useEffect, useCallback } from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import type { User } from '../types';
import { Role } from '../types';
import { api, LoginCredentials, RegisterData, UserProfileData } from '../services/api';
import { NotificationProvider } from './NotificationContext';
import { useCart } from './CartContext';

const { useNavigate } = ReactRouterDOM as any;

const SESSION_TIMEOUT_MINUTES = 30;
const REMEMBER_ME_TIMEOUT_DAYS = 7;
const LAST_ACTIVITY_KEY = 'eservices:lastActivityAt';
const REMEMBER_ME_KEY = 'eservices:rememberMe';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<User | { requires2FA: true }>;
  loginWithGoogle: () => Promise<void>;
  logout: () => void;
  register: (data: RegisterData) => Promise<void>;
  updateUserBalance: (newBalance: number) => void;
  updateUserProfile: (data: UserProfileData) => Promise<void>;
  setUser: (user: User | null) => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { cartItems } = useCart();

  const checkUser = useCallback(async () => {
    setLoading(true);
    try {
      const currentUser = await api.getCurrentUser();

      if (typeof window !== 'undefined') {
        const lastActivityStr = window.localStorage.getItem(LAST_ACTIVITY_KEY);
        const rememberMe = window.localStorage.getItem(REMEMBER_ME_KEY) === 'true';
        const now = Date.now();

        const maxInactivityMs = rememberMe
          ? REMEMBER_ME_TIMEOUT_DAYS * 24 * 60 * 60 * 1000
          : SESSION_TIMEOUT_MINUTES * 60 * 1000;

        if (lastActivityStr) {
          const lastActivity = Number(lastActivityStr);
          if (Number.isFinite(lastActivity) && now - lastActivity > maxInactivityMs) {
            await api.logout();
            window.localStorage.removeItem(LAST_ACTIVITY_KEY);
            window.localStorage.removeItem(REMEMBER_ME_KEY);
            setUser(null);
            setLoading(false);
            navigate('/login', { replace: true, state: { reason: 'session_expired' } });
            return;
          }
        }

        if (currentUser && !lastActivityStr) {
          window.localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
        }
      }

      setUser(currentUser);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUser();
  }, [checkUser]);

  const login = async (credentials: LoginCredentials): Promise<User | { requires2FA: true }> => {
    const result = await api.login(credentials);
    
    // Si le résultat indique qu'on a besoin de 2FA, le retourner tel quel
    if (result && typeof result === 'object' && 'requires2FA' in result && result.requires2FA) {
      return result;
    }
    
    // Safety check: ensure we actually got a user back before accessing properties
    if (!result || typeof result !== 'object' || !('id' in result)) {
        throw new Error("Impossible de récupérer les informations de l'utilisateur.");
    }

    const loggedInUser = result as User;

    if (typeof window !== 'undefined') {
      const remember = !!credentials.rememberMe;
      window.localStorage.setItem(REMEMBER_ME_KEY, String(remember));
      window.localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    }

    setUser(loggedInUser);

    // Client sans email confirmé : redirection vers la validation email (sauf comptes test Play Store)
    if (loggedInUser.role === Role.CLIENT && loggedInUser.email_verified !== true && loggedInUser.is_play_store_test_account !== true) {
      navigate('/verify-email', { replace: true, state: { email: loggedInUser.email ?? '' } });
      return loggedInUser;
    }

    // If user had items in cart as guest, redirect to checkout
    if (cartItems.length > 0) {
        navigate('/dashboard/new-order');
        return loggedInUser;
    }

    if (loggedInUser.role === Role.ADMIN) {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }

    return loggedInUser;
  };

  const loginWithGoogle = async () => {
    await api.loginWithGoogle();
    // La redirection se fait automatiquement vers Google, puis vers /oauth/callback
  };

  const register = async (data: RegisterData) => {
    await api.register(data);
    // Ne pas définir l'utilisateur car il n'est pas encore confirmé
    // Ne pas rediriger ici - laisser Register.tsx gérer l'affichage du message de succès
    // L'utilisateur sera redirigé vers login via le bouton dans le message de succès
  };

  const logout = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(LAST_ACTIVITY_KEY);
      window.localStorage.removeItem(REMEMBER_ME_KEY);
    }
    api.logout();
    setUser(null);
    navigate('/login');
  };
  
  const updateUserBalance = (newBalance: number) => {
    if(user){
        setUser({...user, solde: newBalance});
    }
  };

  const updateUserProfile = async (data: UserProfileData) => {
    if (user) {
        // api.updateUserProfile signature takes merged data
        const updatedUser = await api.updateUserProfile({ ...data, id: user.id });
        setUser(updatedUser);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateLastActivity = () => {
      if (!user) return;
      window.localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    };

    const windowEvents: (keyof WindowEventMap)[] = [
      'click',
      'keydown',
      'mousemove',
      'scroll',
      'touchstart',
    ];
    windowEvents.forEach((evt) => window.addEventListener(evt, updateLastActivity));
    document.addEventListener('visibilitychange', updateLastActivity);

    if (user && !window.localStorage.getItem(LAST_ACTIVITY_KEY)) {
      window.localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    }

    return () => {
      windowEvents.forEach((evt) => window.removeEventListener(evt, updateLastActivity));
      document.removeEventListener('visibilitychange', updateLastActivity);
    };
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!user) return;

    const intervalId = window.setInterval(async () => {
      const lastActivityStr = window.localStorage.getItem(LAST_ACTIVITY_KEY);
      if (!lastActivityStr) return;

      const lastActivity = Number(lastActivityStr);
      if (!Number.isFinite(lastActivity)) return;

      const rememberMe = window.localStorage.getItem(REMEMBER_ME_KEY) === 'true';
      const now = Date.now();

      const maxInactivityMs = rememberMe
        ? REMEMBER_ME_TIMEOUT_DAYS * 24 * 60 * 60 * 1000
        : SESSION_TIMEOUT_MINUTES * 60 * 1000;

      if (now - lastActivity > maxInactivityMs) {
        window.localStorage.removeItem(LAST_ACTIVITY_KEY);
        window.localStorage.removeItem(REMEMBER_ME_KEY);
        await api.logout();
        setUser(null);
        navigate('/login', { replace: true, state: { reason: 'session_expired' } });
      }
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [user, navigate]);


  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, logout, register, updateUserBalance, updateUserProfile, setUser }}>
      <NotificationProvider>
        {children}
      </NotificationProvider>
    </AuthContext.Provider>
  );
};
